import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip
} from '@mui/material';
import { GridFooter, GridFooterContainer, GridToolbarExport } from '@mui/x-data-grid-pro';
import { useEffect, useState } from 'react';
import { PopupComponent } from 'src/components/common/PopupComponent';
import { usePermissionWrapper } from 'src/components/hooks/usePermissionWrapper';
import { useRemoveInTrailPatientDataMutation, useGetEligiblePatientDataMutation } from 'src/services/eligiblePatients';
import { useAppDispatch } from 'src/store';
import { setMessage } from 'src/store/Message';
import staticData from '../../../../resources/static.json';
import { useUser } from 'src/components/hooks/useUser';
import { useTab } from 'src/components/hooks/useTab';
import { setBackButtonPTN, setIntrialPatientInfo, setSelectedIntrialPatient } from 'src/store/SelectedIntrialPatient';
import { setSelectedHierarchy } from 'src/store/SelectedHierarchy';
import { usePreferedDateFormat } from 'src/util/DateFormatter';
import CheckIcon from '@mui/icons-material/Check';
import Link from '@mui/material/Link';
import { DataTable } from '@pihealth/common-ui-library';

import moment from 'moment';
import Loader from 'src/components/common/Loader';

const CustomFooterButtons = (handleClickOpen: any, selectionModel: any) => {
  const { checkPermission } = usePermissionWrapper();
  return (
    <GridFooterContainer>
      <Box sx={{ p: 1, display: 'flex', columnGap: 1 }}>
        {checkPermission('remove_intrail_patients') && (
          <Tooltip title="Patients with subject ID are not allowed to remove from trial">
            <Button variant="outlined" onClick={handleClickOpen} disabled={!selectionModel.length}>
              Remove
            </Button>
          </Tooltip>
        )}
      </Box>
      <GridFooter />
    </GridFooterContainer>
  );
};

export default function InTrailPaitients() {
  const [rows, setRows] = useState([]);
  const [pageSize, setPageSize] = useState(10);
  const [rowsCount, setRowsCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(0);

  const dispatch = useAppDispatch();
  const [selectionModel, setSelectionModel]: any = useState([]);
  const [open, setOpen] = useState(false);
  const [cnfOpen, setCnfOpen] = useState(false);
  const [removeData, setRemoveData]: any = useState([]);
  const tableHeadCell = staticData.in_trail_patient_data.PopUp_data.table_cell;
  const [getInTrialPatientsData, { data, isError, error, isLoading }] = useGetEligiblePatientDataMutation();
  const { user } = useUser();
  const { setTabVal, tabsData } = useTab();
  const [
    removeIntrailAPI,
    { isError: removeInTrailIsError, isSuccess: removeInTrailDataIsSuccess, error: removeInTrailError }
  ] = useRemoveInTrailPatientDataMutation();

  const { userPreferedDateFormat, preferedDateFormat } = usePreferedDateFormat();

  const [filterObject, setFilterObject] = useState<any>();

  const [columns, setColumns] = useState([]);
  useEffect(() => {
    if (data) {
      setRowsCount(data.record_count);
      const tempColumns: any = [
        {
          field: 'Core_FICS_patient_identifier',
          headerName: 'Core FICS Patient Identifier',
          minWidth: 90,
          flex: 1
        },
        {
          field: 'sex',
          headerName: 'Sex',
          minWidth: 70,
          flex: 1
        },
        {
          field: 'date_of_birth',
          headerName: 'Date of Birth',
          minWidth: 100,
          flex: 1,
          type: 'date'
        },
        {
          field: 'eligible_trials',
          headerName: 'Eligible Trials',
          width: 150,
          renderCell: (params: any) => {
            return (
              <>
                {params.row.eligible_trials.length > 1 ? (
                  <Stack direction="column" spacing={0} className="fakeRowColSpan withLeftBorder">
                    {params.row.eligible_trials.map((trial: any) => (
                      <Box key={`eligible_trials_${trial}`} className="tableCell">
                        {trial}
                      </Box>
                    ))}
                  </Stack>
                ) : (
                  <Stack direction="column" spacing={0} className="fakeRowColSpan">
                    <Box className="tableCell">{params.row.eligible_trials[0]}</Box>
                  </Stack>
                )}
              </>
            );
          }
        }
      ];
      if (data?.selected_data.length > 0) {
        let maxLenth = 0;
        data?.selected_data.map((criteriaList: any) => {
          criteriaList.criteria.map((criteriaObjs: any) => {
            if (typeof criteriaObjs == 'object') {
              const criteriaObj = Object.values(criteriaObjs);
              criteriaObj.forEach((item: any) => {
                item.forEach((lists: any) => {
                  const nestedList: any = Object.values(lists);
                  let totalLength = 0;
                  for (const i of nestedList) {
                    totalLength += i.length;
                  }
                  if (maxLenth < totalLength) {
                    maxLenth = totalLength;
                  }
                });
              });
            }
          });
        });
        const criteraArray: any = data?.selected_data.map((item: any, index: any) => item.criteria.length);
        const maxCriteriaObjectIndex = criteraArray.indexOf(Math.max(...criteraArray));
        if (maxCriteriaObjectIndex >= 0) {
          if (data?.selected_data) {
            const criteriaColumns: any = [];
            for (let i = 0; i < maxLenth; i++) {
              criteriaColumns.push({
                field: `criteria_${Number(i + 1)}`,
                headerName: `Criteria ${Number(i + 1)}`,
                width: 90,
                renderCell: (params: any) => {
                  const multiStudyData: any = Object.values(
                    Object.values(params.row.criteria.map((i: any) => (Object.values(i)[0] as any).flat())).map(
                      (item: any) => item[0]
                    )
                  );
                  const toolTipDataMultiStudy = multiStudyData.map((item: any) => Object.values(item).flat());
                  return (
                    <>
                      {params.row.criteria.length > 1 ? (
                        <Stack direction="column" spacing={0} className="fakeRowColSpan">
                          {toolTipDataMultiStudy.map((item: any) => {
                            return item[i] ? (
                              <Box
                                className="tableCell alignCenter"
                                key={`${params.row.eligible_trials[i]}-${item[i].value}`}
                              >
                                <Tooltip
                                  title={`${item[i].field} ${item[i].operator.replaceAll('_', ' ')} ${
                                    item[i].type === 'DATE' ? preferedDateFormat(item[i].value) : item[i].value
                                  }`}
                                  placement="top"
                                  className="smallfontTooltip"
                                >
                                  <CheckIcon color="primary" />
                                </Tooltip>
                              </Box>
                            ) : (
                              <Box className="tableCell alignCenter emptyGrey" />
                            );
                          })}
                        </Stack>
                      ) : (
                        <>
                          {toolTipDataMultiStudy.map((item: any) => {
                            return item[i] ? (
                              <Stack direction="column" spacing={0} className="fakeRowColSpan">
                                <Box className="tableCell alignCenter">
                                  <Tooltip
                                    title={`${item[i].field} ${item[i].operator.replaceAll('_', ' ')} ${
                                      item[i].type === 'DATE' ? preferedDateFormat(item[i].value) : item[i].value
                                    }`}
                                    placement="top"
                                    className="smallfontTooltip"
                                  >
                                    <CheckIcon color="primary" />
                                  </Tooltip>
                                </Box>
                              </Stack>
                            ) : (
                              <Box className="tableCell alignCenter emptyGrey" />
                            );
                          })}
                        </>
                      )}
                    </>
                  );
                },
                valueGetter: (params: any) => {
                  const multiStudyData: any = Object.values(
                    Object.values(params.row.criteria.map((i: any) => (Object.values(i)[0] as any).flat())).map(
                      (item: any) => item[0]
                    )
                  );
                  const toolTipDataMultiStudy = multiStudyData?.map((item: any) => Object.values(item).flat());
                  const temp = toolTipDataMultiStudy
                    .map((item: any) =>
                      item[i] ? `${item[i].field} ${item[i].operator.replaceAll('_', ' ')} ${item[i].value}` : null
                    )
                    .filter((item: any) => item)
                    .join();
                  return temp;
                }
              });
            }
            tempColumns.push(...criteriaColumns);
          }
          const temp = data?.selected_data.map((elem: any) => {
            const EligibleComments = elem.comments.comment;
            return {
              id: elem.patient?.parent_id ? elem.patient?.parent_id : Math.random(),
              parent_id: elem.patient?.parent_id,
              Core_FICS_patient_identifier: elem.patient?.Core_FICS_patient_identifier,
              sex: elem.patient.sex,
              date_of_birth: moment(elem.patient.date_of_birth).format(`${userPreferedDateFormat.toUpperCase()}`),
              eligible_trials: elem.eligible_trials,
              comments: EligibleComments,
              criteria: elem.criteria,
              study_hierarchy: elem.study_hierarchy,
              add_to_trial: elem.eligible_trials
            };
          });
          setRows(temp);
        }
      } else {
        setRows([]);
      }
      tempColumns.push({
        field: 'add_to_trial',
        headerName: 'Add to Trial',
        minWidth: 140,
        flex: 1,
        renderCell: (params: any) => {
          return (
            <>
              {params.row.eligible_trials.length > 1 ? (
                <Stack direction="column" spacing={0} className="fakeRowColSpan withLeftBorder">
                  {params.row.eligible_trials.map((trial: any, index: any) => (
                    <>
                      {' '}
                      <Box key={`eligible_trials_${trial}`} className="tableCell">
                        <Link
                          onClick={() => addToTrail(params.row, index)}
                          sx={{
                            cursor: 'pointer',
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            '-webkitLineClamp': '2',
                            '-webkitBoxOrient': 'vertical'
                          }}
                        >
                          {trial}
                        </Link>
                      </Box>
                    </>
                  ))}
                </Stack>
              ) : (
                <Stack direction="column" spacing={0} className="fakeRowColSpan">
                  <>
                    {' '}
                    <Box className="tableCell">
                      <Link
                        onClick={() => addToTrail(params.row, 0)}
                        sx={{
                          cursor: 'pointer',
                          whiteSpace: 'normal',
                          wordBreak: 'break-word',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          '-webkitLineClamp': '2',
                          '-webkitBoxOrient': 'vertical'
                        }}
                      >
                        {params.row.eligible_trials[0]}
                      </Link>
                    </Box>
                  </>
                </Stack>
              )}
            </>
          );
        }
      });
      setColumns(tempColumns);
    }
  }, [data, userPreferedDateFormat]);

  const addToTrail = (data: any, index: any) => {
    dispatch(
      setSelectedHierarchy({
        hierarchySponsor: 'PiHealth',
        hierarchyStudy: data.study_hierarchy.study_id,
        hierarchyCountry: data.study_hierarchy.country,
        hierarchySite: data.study_hierarchy.site_id
      })
    );
    const patientData = {
      parent_id: data.parent_id,
      Core_FICS_patient_identifier: data.Core_FICS_patient_identifier,
      prescreen_id: data.prescreen_id,
      sex: data.sex,
      date_of_birth: data.date_of_birth,
      eligible_trials: data.eligible_trials[index],
      add_to_trial: data.add_to_trial
    };
    dispatch(setBackButtonPTN(true));
    dispatch(setSelectedIntrialPatient(data.Core_FICS_patient_identifier));
    dispatch(setIntrialPatientInfo(patientData));
    navigateToPatientTrialNotebook();
  };

  useEffect(() => {
    if (pageSize === 10 || pageSize === 20 || pageSize === 30) {
      if (pageNumber * pageSize > rowsCount - 1) {
        setPageNumber(0);
        getInTrialPatientsData({
          data: filterObject,
          status: 'In Trial',
          from: 0,
          size: pageSize
        });
      } else {
        getInTrialPatientsData({
          data: filterObject,
          status: 'In Trial',
          from: pageNumber * pageSize,
          size: pageSize
        });
      }
    }
  }, [pageNumber, pageSize, filterObject]);

  useEffect(() => {
    if (isError || removeInTrailIsError) {
      if (isError) {
        setRows([]);
        setRowsCount(0);
      }
      const errorData: any = error ?? removeInTrailError;
      if (errorData?.status == 500) {
        dispatch(
          setMessage({
            msgDetails: `${errorData?.status} : Internal Server Error`,
            severity: 'error'
          })
        );
      } else if (errorData?.status == 403) {
        dispatch(
          setMessage({
            msgDetails: 'Error 403 Restricted User permission',
            severity: 'error'
          })
        );
      }
      setCnfOpen(false);
      setOpen(false);
    }
  }, [isError, removeInTrailIsError]);
  useEffect(() => {
    if (removeInTrailDataIsSuccess) {
      dispatch(
        setMessage({
          msgDetails: `${removeData.length} subjects removed from trial`,
          severity: 'success'
        })
      );
      getInTrialPatientsData({
        data: filterObject,
        status: 'In Trial',
        from: pageNumber * pageSize,
        size: pageSize
      });
      setCnfOpen(false);
      setOpen(false);
      setRemoveData([]);
    }
  }, [removeInTrailDataIsSuccess]);

  const navigateToPatientTrialNotebook = () => {
    const currentTab: any = tabsData.find((elem: any) => elem.key === 'patientTrialNoteBook');
    if (currentTab?.index) {
      setTabVal(currentTab?.index);
    }
  };

  const [isSubjectIDPationThere, setIsSubjectIDPationThere] = useState<boolean>(false);
  const handleClickOpen = () => {
    if (selectionModel.length <= 10) {
      const tempData = data.selected_data
        .filter((ele: any) => {
          return selectionModel.includes(ele.patient.parent_id);
        })
        .sort((trailObj1: any, trailObj2: any) => {
          const obj1 = trailObj1.study_hierarchy?.study_id.toLowerCase(),
            obj2 = trailObj2.study_hierarchy?.study_id.toLowerCase();

          if (obj1 < obj2) {
            return -1;
          }
          if (obj1 > obj2) {
            return 1;
          }
          return 0;
        });
      const findPationtWithoutSubjectID = tempData
        .filter((ele: any) => ele.patient.subject_id === null)
        .map((ele: any) => {
          return { ...ele, error: false };
        });
      const findPationtWithSubjectID = tempData.filter((ele: any) => ele.patient.subject_id !== null);
      if (findPationtWithSubjectID?.length) {
        setIsSubjectIDPationThere(true);
      } else {
        setIsSubjectIDPationThere(false);
      }
      setRemoveData(findPationtWithoutSubjectID);
      setOpen(true);
    } else {
      dispatch(
        setMessage({
          msgDetails: 'Maximum 10 patients are allowed.',
          severity: 'error'
        })
      );
    }
  };
  const handleChange = (id: any, e: any) => {
    const updateArr = removeData.map((obj: any) => {
      if (id === obj.patient.Core_FICS_patient_identifier) {
        return { ...obj, [e.target.id]: e.target.value };
      } else {
        return obj;
      }
    });
    setRemoveData(updateArr);
  };
  const handleError = (id: any, e: any) => {
    const updateArr = removeData.map((obj: any) => {
      if (id === obj.patient.Core_FICS_patient_identifier) {
        return { ...obj, error: e };
      } else {
        return obj;
      }
    });
    setRemoveData(updateArr);
  };

  const onSelectionModelChange = (ids: any) => {
    if (ids.length > 10) {
      dispatch(
        setMessage({
          msgDetails: 'Maximum 10 patients are allowed.',
          severity: 'error'
        })
      );
    } else {
      setSelectionModel(ids);
    }
  };
  const handleClose = () => {
    setOpen(false);
  };
  const handleSubmit = () => {
    const tempArr = removeData.map((ele: any) => {
      return {
        parent_id: ele.patient.parent_id,
        trial_id: ele.study_hierarchy.study_id,
        comments: ele.comments
      };
    });
    const dataToSend = {
      patient_update_details: tempArr,
      updated_by: user
    };
    removeIntrailAPI({
      data: dataToSend
    });
  };
  const handleDelete = (ele: any) => {
    const updatedArr = removeData.filter(
      (obj: any) => obj.patient.Core_FICS_patient_identifier !== ele.Core_FICS_patient_identifier
    );
    if (updatedArr.length === 0) {
      handleClose();
    }
    setRemoveData(updatedArr);
  };

  const handleCnfClose = () => {
    setCnfOpen(false);
  };
  const handelOpencnf = () => {
    let found = false;
    const updatedData = removeData.map((element: any) => {
      if (element?.comments === undefined || element?.comments?.length === 0) {
        found = true;
        return { ...element, error: true };
      } else {
        return { ...element, error: false };
      }
    });
    setRemoveData(updatedData);
    if (found) {
      dispatch(
        setMessage({
          msgDetails: 'Missing mandatory information',
          severity: 'error'
        })
      );
    } else {
      setCnfOpen(true);
    }
  };

  const definedCustomDatGridExport = (exportFields: any = {}) => {
    return (
      <GridToolbarExport
        csvOptions={{
          fileName: `In Trail ${moment(new Date()).format(
            `${(userPreferedDateFormat.includes('/')
              ? userPreferedDateFormat.replaceAll('/', '_')
              : userPreferedDateFormat.replaceAll('-', '_')
            ).toUpperCase()} hh_mm A`
          )}`
        }}
      />
    );
  };

  const filterValues = (data: any) => {
    setFilterObject(data);
  };

  return (
    <div>
      <div style={{ display: 'flex', height: '100%' }}>
        <DataTable
          height="76vh"
          columns={columns}
          rows={rows}
          loading={isLoading}
          CustomFooter={() => CustomFooterButtons(handleClickOpen, selectionModel)}
          hasDefinedColumns
          datePreference={userPreferedDateFormat}
          checkboxSelection
          checkboxSelectionVisibleOnly
          selectionModel={selectionModel}
          onRowsSelection={onSelectionModelChange}
          disableSelectionOnClick
          pagination
          paging
          paginationMode="server"
          rowsCount={rowsCount}
          page={pageNumber}
          pageSize={pageSize}
          setPageNumber={setPageNumber}
          setPageSize={setPageSize}
          dataTableInitialState={{
            sorting: {
              sortModel: [{ field: 'Core_FICS_patient_identifier', sort: 'asc' }]
            }
          }}
          hasDefinedDataGridToolbarExport
          definedGridToolbarExport={definedCustomDatGridExport}
          filterValues={(data: any) => filterValues(data)}
        />
      </div>
      <PopupComponent open={open} maxWidth="md">
        <Loader />
        <DialogTitle>Patients Trial Removal</DialogTitle>
        <DialogContent className="tsda_prescreenDialog Fail">
          {isSubjectIDPationThere ? <Alert severity="info">Patients with subject ID is not considered</Alert> : null}
          <TableContainer component={Paper} elevation={0} sx={{ maxHeight: '50vh', overflowY: 'auto' }}>
            <Table
              sx={{ minWidth: '100%' }}
              stickyHeader
              aria-label="Patients Trial Removal"
              className="tableWFontStyle SecColBold"
            >
              <TableHead>
                <TableRow>
                  {tableHeadCell.map(tableHeadData => (
                    <TableCell component="th" key={tableHeadData}>
                      {tableHeadData}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {removeData.map((ele: any, index: any) => (
                  <TableRow key={ele.patient.Core_FICS_patient_identifier}>
                    <TableCell sx={{ width: '210px' }}>{ele.patient.Core_FICS_patient_identifier}</TableCell>
                    <TableCell sx={{ width: '150px' }}>{ele?.study_hierarchy?.study_id}</TableCell>
                    <TableCell sx={{ width: '300px' }}>
                      <TextField
                        id="comments"
                        label="Reason for Patients Trial Removal"
                        multiline
                        fullWidth
                        required
                        onChange={e => handleChange(ele.patient.Core_FICS_patient_identifier, e)}
                        maxRows={3}
                        variant="standard"
                        sx={{ mt: -1.75 }}
                        inputProps={{ maxLength: 250 }}
                        onBlur={e => {
                          if (e.target.value) {
                            handleError(ele.patient.Core_FICS_patient_identifier, false);
                          } else {
                            handleError(ele.patient.Core_FICS_patient_identifier, true);
                          }
                        }}
                        error={ele.error}
                        helperText={ele.error ? 'Required' : ''}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton onClick={() => handleDelete(ele.patient)}>
                        <CloseIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {!removeData.length ? <center style={{ marginTop: '12px' }}>No Data</center> : null}
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} className="grey-btn">
            Cancel
          </Button>
          <Button onClick={handelOpencnf} disabled={!removeData.length}>
            Submit
          </Button>
        </DialogActions>
      </PopupComponent>
      <PopupComponent open={cnfOpen} maxWidth="md" child>
        <DialogTitle>Are you sure you want to remove the selected patients from trial?</DialogTitle>
        <DialogActions>
          <Button onClick={handleCnfClose} className="grey-btn">
            No
          </Button>
          <Button onClick={handleSubmit}>Yes</Button>
        </DialogActions>
      </PopupComponent>
    </div>
  );
}
